// Mirrors BE ownership.errors.ts; renaming breaks older FEs in the wild.
export const OwnershipErrorCode = {
  POOL_EXCEEDED: 'OWNERSHIP_POOL_EXCEEDED',
  ADD_ZERO_PERCENT_REQUIRES_HAS_SIGNIFICANT_CONTROL: 'ADD_ZERO_PERCENT_REQUIRES_HAS_SIGNIFICANT_CONTROL',
  EDIT_PERCENT_ZERO_UNSUPPORTED: 'EDIT_PERCENT_ZERO_UNSUPPORTED',
  OWNERSHIP_CYCLE: 'OWNERSHIP_CYCLE',
  SELF_SHAREHOLDING: 'SELF_SHAREHOLDING',
  SOURCE_CORP_DIFFERENT_WORKSPACE: 'SOURCE_CORP_DIFFERENT_WORKSPACE',
  EMPTY_PATCH: 'EMPTY_PATCH',
  MUTUALLY_EXCLUSIVE_FIELDS: 'MUTUALLY_EXCLUSIVE_FIELDS',
  DIRECTOR_NOT_SOFT_DELETED: 'DIRECTOR_NOT_SOFT_DELETED',
  HSC_REQUIRES_CONTROL_TYPE: 'HSC_REQUIRES_CONTROL_TYPE',
  CONTROL_TYPE_REQUIRES_HSC: 'CONTROL_TYPE_REQUIRES_HSC',
  INHERITED_UBOS_REQUIRES_HSC: 'INHERITED_UBOS_REQUIRES_HSC',
  DIRECTOR_MISSING_NATIONALITY: 'DIRECTOR_MISSING_NATIONALITY',
  SHARE_CLASS_HAS_SHAREHOLDINGS: 'SHARE_CLASS_HAS_SHAREHOLDINGS',
  DUPLICATE_IN_BATCH: 'DUPLICATE_IN_BATCH',
  OWNERSHIP_UNSUPPORTED: 'OWNERSHIP_UNSUPPORTED',
} as const;

export type OwnershipErrorCodeValue =
  (typeof OwnershipErrorCode)[keyof typeof OwnershipErrorCode];

export class OwnershipApiError extends Error {
  readonly code?: OwnershipErrorCodeValue;
  readonly field?: string;
  readonly details?: Record<string, unknown>;
  readonly statusCode: number;

  constructor(args: {
    statusCode: number;
    message: string;
    code?: OwnershipErrorCodeValue;
    field?: string;
    details?: Record<string, unknown>;
  }) {
    super(args.message);
    this.name = 'OwnershipApiError';
    this.statusCode = args.statusCode;
    this.code = args.code;
    this.field = args.field;
    this.details = args.details;
  }
}

interface ParsedErrorBody {
  // Nest class-validator sends string[]; ownershipBadRequest sends string.
  message?: string | string[];
  code?: string;
  field?: string;
  details?: Record<string, unknown>;
}

const normalizeMessage = (
  raw: string | string[] | undefined,
  fallback: string,
): string => {
  if (typeof raw === 'string' && raw.length > 0) return raw;
  if (Array.isArray(raw) && raw.length > 0) return raw.join('. ');
  return fallback;
};

export const toOwnershipApiError = async (
  response: Response,
  fallbackMessage: string,
): Promise<OwnershipApiError> => {
  let parsed: ParsedErrorBody = {};
  try {
    const body = (await response.json()) as unknown;
    if (body && typeof body === 'object') {
      parsed = body as ParsedErrorBody;
    }
  } catch {
    // Non-JSON body
  }
  return new OwnershipApiError({
    statusCode: response.status,
    message: normalizeMessage(parsed.message, fallbackMessage),
    code: parsed.code as OwnershipErrorCodeValue | undefined,
    field: typeof parsed.field === 'string' ? parsed.field : undefined,
    details:
      parsed.details && typeof parsed.details === 'object'
        ? parsed.details
        : undefined,
  });
};
